import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IPage } from '../../../models/page';
import useTranslations from '../../../hooks/useTranslations';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import Alert from '../../../components/UI/Alert/Alert';
import BackButton from '../../../components/UI/BackButton/BackButton';
import { VouchersActions } from '../../../store/entities/vouchers/vouchers.actions';
import { CONSTANTS } from '../../../utils/constants';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import useSiteOptions from '../../../hooks/useSiteOptions';
import useVoucherDynamicOptions from '../../../hooks/useVoucherDynamicOptions';
import {
  vouchersDetailsAddValidation,
  vouchersDetailsEditValidation,
  vouchersDetailsAddChildrenPhoneNumberValidation,
} from '../../../utils/formValidation/vouchers';
import AddPhonesNumbersFromFile from './AddPhonesNumbersFromFile/AddPhonesNumbersFromFile';
import { useAppSelector } from '../../../store';

const DEFAULT_DURATION_IN_MONTHS = 12;

const VoucherDetails = ({ permission, nextRoute }: IPage) => {
  const [show, setShow] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const siteList = useSiteOptions();
  const { types: typeList } = useVoucherDynamicOptions(false);
  const [loadedVoucher, setLoadedVoucher] = useState<any | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  usePermissionRedirect(permission, nextRoute);
  const { formikProps } = useAppSelector((store) => store.form);

  const { voucherId = '' } = useParams();
  const isEdit = !!voucherId;

  useEffect(() => {
    return () => {
      setShow(false);
    };
  }, []);

  // Load voucher on edit
  useEffect(() => {
    const loadVoucher = async () => {
      if (!isEdit) return;
      const voucher: any = await dispatch(
        VouchersActions.getVoucherById(Number(voucherId))
      );
      if (voucher) setLoadedVoucher(voucher);
    };
    loadVoucher();
  }, [dispatch, isEdit, voucherId]);

  const validation = useMemo(
    () =>
      isEdit ? vouchersDetailsEditValidation : vouchersDetailsAddValidation,
    [isEdit]
  );

  const mergeImportedPhoneNumbers = useCallback(
    (imported: string[]) => {
      if (!formikProps) return;
      formikProps.setFieldValue('phoneNumbers', imported, true);
    },
    [formikProps]
  );

  const fields: IFormFields = useMemo(() => {
    return {
      initialValues: {
        type: loadedVoucher?.type ?? '',
        siteId: loadedVoucher?.siteId ?? '',
        expiryDate: loadedVoucher?.expiryDate ?? '',
        durationInMonths:
          loadedVoucher?.durationInMonths ?? DEFAULT_DURATION_IN_MONTHS,
        customerPhoneNumber: loadedVoucher?.customerPhoneNumber ?? '',
        phoneNumbers: [],
      },
      formData: [
        ...(isEdit
          ? []
          : [
              {
                type: FieldTypes.AUTOCOMPLETE,
                id: 'type',
                label: rcTranslate('vouchers.vouchersDetails.fieldNames.type'),
                items: typeList,
                props: { multiple: false },
              },
            ]),
        ...[
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'siteId',
            label: rcTranslate('vouchers.vouchersDetails.fieldNames.site'),
            items: siteList,
            props: { multiple: false },
          },
          {
            type: FieldTypes.DATE,
            id: 'expiryDate',
            label: rcTranslate(
              'vouchers.vouchersDetails.fieldNames.expiryDate'
            ),
            props: { getFullDayTime: true, numOfDays: 1 },
          },
          {
            type: FieldTypes.NUMBER,
            id: 'durationInMonths',
            label: rcTranslate(
              'vouchers.vouchersDetails.fieldNames.durationInMonths'
            ),
            props: {},
          },
        ],
        ...(isEdit
          ? [
              {
                type: FieldTypes.TEL,
                id: 'customerPhoneNumber',
                label: rcTranslate('vouchers.filters.customerPhoneNumber'),
                props: {},
              },
            ]
          : [
              {
                type: FieldTypes.STRING_ARRAY,
                id: 'phoneNumbers',
                label: (
                  <>
                    {rcTranslate(
                      'vouchers.vouchersDetails.fieldNames.phoneNumbers'
                    )}
                    <AddPhonesNumbersFromFile
                      onMerge={mergeImportedPhoneNumbers}
                    />
                  </>
                ),
                props: {
                  showChildLabels: true,
                  childLabelPrefix: rcTranslate(
                    'vouchers.filters.customerPhoneNumber'
                  ),
                  ...vouchersDetailsAddChildrenPhoneNumberValidation,
                },
              },
            ]),
      ],
    } as IFormFields;
  }, [isEdit, loadedVoucher, rcTranslate, siteList, typeList]);

  const onSubmit = async (values: any, setSubmitting: (v: boolean) => void) => {
    let res: any = null;
    if (isEdit) {
      const body = {
        siteId: values.siteId,
        expiryDate: values.expiryDate,
        durationInMonths: values.durationInMonths,
        customerPhoneNumber: values.customerPhoneNumber
          ? String(values.customerPhoneNumber).trim()
          : null,
      };

      res = await dispatch(
        VouchersActions.updateVoucher(Number(voucherId), body)
      );
      setAlertMessage(rcTranslate('vouchers.vouchersDetails.updateSuccess'));
    } else {
      const body = {
        type: values.type,
        siteId: values.siteId,
        expiryDate: values.expiryDate,
        durationInMonths: values.durationInMonths,
        phoneNumbers: Array.isArray(values.phoneNumbers)
          ? values.phoneNumbers
              .map((s: any) => String(s).trim())
              .filter(Boolean)
          : null,
      };
      res = await dispatch(VouchersActions.addVouchers(body));

      let failedCreationAlert = '';
      let failedMessagesAlert = '';
      if (res.failedCreation.length > 0) {
        failedCreationAlert = `\n${rcTranslate('vouchers.vouchersDetails.failedCreation')}\n${res.failedCreation.join('\n')}`;
      }
      if (res.failedMessages.length > 0) {
        failedMessagesAlert = `\n${rcTranslate('vouchers.vouchersDetails.failedMessages')}\n${res.failedMessages.join('\n')}`;
      }
      setAlertMessage(
        `${rcTranslate('vouchers.vouchersDetails.addSuccess')}\n${failedCreationAlert}\n${failedMessagesAlert}`
      );
    }
    setSubmitting(false);
    if (res) {
      setShow(true);
      setTimeout(() => {
        setShow(false);
        navigate(
          '/' +
            CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.VOUCHERS_MANAGEMENT.CHILDREN.VOUCHERS_LIST
        );
      }, 10000);
    }
  };

  return (
    <>
      <BackButton />

      <Form
        fields={fields}
        formValidation={validation}
        onSubmit={onSubmit}
        submitButtonTitle={rcTranslate(
          isEdit
            ? 'vouchers.vouchersDetails.pageTitle.update'
            : 'vouchers.vouchersDetails.pageTitle.add'
        )}
        entityIdentifier={
          isEdit
            ? {
                label: 'vouchers.vouchersDetails.entityIdentifier',
                id: voucherId,
              }
            : undefined
        }
      />
      {show && <Alert message={alertMessage} color="success" />}
    </>
  );
};

export default VoucherDetails;
